// src/features/terms-of-use/components/LegalCard.tsx
"use client";

import React from "react";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils/utils";

interface LegalCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  className?: string;
  delay?: number;
}

const LegalCard = ({
  title,
  description,
  icon: Icon,
  className,
  delay = 0,
}: LegalCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -5 }}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-black/5 bg-white p-8 transition-shadow hover:shadow-2xl hover:shadow-black/5",
        "flex flex-col gap-4",
        className,
      )}
    >
      {/* Background Watermark Icon */}
      <div className="absolute -bottom-6 -right-6 opacity-[0.03] transition-transform duration-700 group-hover:scale-110 group-hover:-rotate-12">
        <Icon size={180} />
      </div>

      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon size={24} />
      </div>

      <div className="relative z-10 space-y-3">
        <h3 className="text-xl font-bold tracking-tight text-black">{title}</h3>
        <p className="text-sm leading-relaxed text-muted-foreground lg:text-base">
          {description}
        </p>
      </div>

      {/* Subtle Hover Border Accent */}
      <div className="absolute inset-0 border-2 border-transparent transition-colors group-hover:border-primary/20 rounded-2xl pointer-events-none" />
    </motion.div>
  );
};

export default LegalCard;
